import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthLayout } from '@/widgets/auth-layout'
import type { AuthLayoutPanel } from '@/widgets/auth-layout'
import {
  buildRegistrationStepPath,
  clearRegistrationProfileStepUnlock,
  isProfileStepRequired,
  pickAuthRedirectSearchParams,
  parseAuthError,
  readRegistrationProfileStepUnlock,
  readRegistrationStep,
  resolveAuthRedirect,
  unlockRegistrationProfileStep,
  useAuth,
} from '@/features/auth'
import bgEpic from '@/shared/assets/registration/bg_epic.png'
import { RegistrationPageDesktop } from './registration-page-desktop'
import { RegistrationPageMobile } from './registration-page-mobile'

const MISSING_PASSWORD_MESSAGE =
  'Аккаунт найден, но пароль еще не установлен. Чтобы создать пароль, нажмите «Забыли пароль?». Также можно войти без пароля через «Вход по ссылке».'
const AGREEMENT_REQUIRED_MESSAGE =
  'Необходимо принять пользовательское соглашение и правила обработки персональных данных.'
const CONSENT_LOOKUP_ERROR_MESSAGE =
  'Не удалось проверить статус согласия. Попробуйте снова или продолжите позже.'
const PROFILE_REQUIRED_MESSAGE = 'Заполните все поля, чтобы продолжить.'
const EMAIL_REQUIRED_MESSAGE = 'Введите email, чтобы продолжить.'
const EMAIL_INVALID_MESSAGE = 'Введите корректный email.'
const PASSWORD_RESET_EMAIL_SENT_MESSAGE =
  'Мы отправили ссылку для сброса пароля на указанный email.'
const MAGIC_LINK_EMAIL_SENT_MESSAGE = 'Мы отправили ссылку для входа на указанный email.'
const EMAIL_CODE_STEP_ENABLED = false
const LAST_AUTH_EMAIL_COOKIE = 'last_auth_email'
const LAST_AUTH_EMAIL_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const normalizeRequiredText = (value: string) => value.trim()

const readCookie = (name: string) => {
  if (typeof document === 'undefined') return null

  const encodedName = encodeURIComponent(name)
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${encodedName}=`))
  if (!cookie) return null

  const value = cookie.slice(encodedName.length + 1)
  if (!value) return null
  return decodeURIComponent(value)
}

const writeCookie = (name: string, value: string, maxAgeSeconds: number) => {
  if (typeof document === 'undefined') return
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`
}

const persistLastAuthEmail = (email: string) => {
  writeCookie(LAST_AUTH_EMAIL_COOKIE, email, LAST_AUTH_EMAIL_COOKIE_MAX_AGE_SECONDS)
}

export function RegistrationPage() {
  const [panel, setPanel] = useState<AuthLayoutPanel>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [telegram, setTelegram] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [company, setCompany] = useState('')
  const [requiresAgreement, setRequiresAgreement] = useState(false)
  const [agreementChecked, setAgreementChecked] = useState(false)
  const [agreementError, setAgreementError] = useState<string | null>(null)
  const [consentLookupError, setConsentLookupError] = useState<string | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)
  const profileDirtyRef = useRef(false)
  const profileInitUserIdRef = useRef<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordPanelMessage, setPasswordPanelMessage] = useState<string | null>(null)
  const [passwordPanelMessageTone, setPasswordPanelMessageTone] = useState<
    'default' | 'success' | 'error'
  >('default')
  const [profileStepUnlocked, setProfileStepUnlocked] = useState(() =>
    readRegistrationProfileStepUnlock()
  )
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const legalLinksReturnTo = `${location.pathname}${location.search}`
  const {
    user,
    isAuthenticated,
    isLoading,
    login,
    getConsentState,
    updateProfile,
    requestPasswordReset,
    requestMagicLink,
  } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const userProfileIncomplete =
    user?.profileComplete === false || (user?.missingProfileFields?.length ?? 0) > 0
  const userRequiresAgreement = user?.acceptedUserAgreement === false

  const buildRegistrationSearchParams = useCallback(
    (targetStep?: 'profile') => {
      const nextParams = pickAuthRedirectSearchParams(searchParams)
      if (targetStep) {
        nextParams.set('step', targetStep)
      }
      return nextParams
    },
    [searchParams]
  )

  useEffect(() => {
    document.title = 'Вход и регистрация | Epic Growth'
  }, [])

  useEffect(() => {
    const savedEmail = readCookie(LAST_AUTH_EMAIL_COOKIE)
    if (!savedEmail) return
    setEmail(savedEmail.trim().toLowerCase())
  }, [])

  useEffect(() => {
    const requestedStep = readRegistrationStep(searchParams.get('step'))
    const profileStepAllowedByNavigationState =
      typeof location.state === 'object' &&
      location.state !== null &&
      'allowProfileStepOnce' in location.state &&
      location.state.allowProfileStepOnce === true
    const canAccessProfileStep =
      !!user && isAuthenticated && (userProfileIncomplete || userRequiresAgreement)

    if (requestedStep === 'profile') {
      if (isLoading) {
        return
      }

      if (!canAccessProfileStep && !profileStepUnlocked && !profileStepAllowedByNavigationState) {
        clearRegistrationProfileStepUnlock()
        setSearchParams(buildRegistrationSearchParams(), { replace: true })
        setPanel('email')
        return
      }

      if (canAccessProfileStep && profileStepUnlocked) {
        clearRegistrationProfileStepUnlock()
        setProfileStepUnlocked(false)
      }

      setPanel('profile')
      return
    }

    setPanel((currentPanel) => (currentPanel === 'profile' ? 'email' : currentPanel))
  }, [
    buildRegistrationSearchParams,
    isAuthenticated,
    isLoading,
    location.state,
    profileStepUnlocked,
    searchParams,
    setSearchParams,
    user,
    userProfileIncomplete,
    userRequiresAgreement,
  ])

  useEffect(() => {
    if (panel !== 'profile' || !user) {
      return
    }

    if (profileDirtyRef.current) {
      return
    }

    if (profileInitUserIdRef.current === user.id) {
      return
    }

    setName(user.name ?? '')
    setTelegram(user.telegram ?? '')
    setCompany(user.company ?? '')
    setJobTitle(user.position ?? '')
    setRequiresAgreement(user.acceptedUserAgreement === false)
    setAgreementChecked(user.acceptedUserAgreement === true)
    profileInitUserIdRef.current = user.id
  }, [panel, user])

  const setRegistrationStep = (target: AuthLayoutPanel) => {
    if (target === 'profile') {
      setSearchParams(buildRegistrationSearchParams('profile'), { replace: true })
      return
    }

    setSearchParams(buildRegistrationSearchParams(), { replace: true })
  }

  const goToPanel = (target: AuthLayoutPanel) => {
    setAuthError(null)
    setAgreementError(null)
    setProfileError(null)
    setEmailError(null)
    setPasswordPanelMessage(null)
    setPasswordPanelMessageTone('default')
    if (target !== 'profile') {
      clearRegistrationProfileStepUnlock()
      setProfileStepUnlocked(false)
      profileDirtyRef.current = false
      profileInitUserIdRef.current = null
    }
    setRegistrationStep(target)
    setPanel(target)
  }

  const handleEmailChange = (value: string) => {
    setAuthError(null)
    setAgreementError(null)
    setConsentLookupError(null)
    setEmailError(null)
    setPasswordPanelMessage(null)
    setPasswordPanelMessageTone('default')
    setEmail(value)
  }

  const handlePasswordChange = (value: string) => {
    setAuthError(null)
    setAgreementError(null)
    setPasswordPanelMessage(null)
    setPasswordPanelMessageTone('default')
    setPassword(value)
  }

  useEffect(() => {
    if (panel !== 'password') {
      return
    }

    const normalizedEmail = email.trim().toLowerCase()
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setRequiresAgreement(false)
      setAgreementChecked(false)
      setConsentLookupError(null)
      return
    }

    let cancelled = false
    setConsentLookupError(null)

    void (async () => {
      try {
        const consentState = await getConsentState(normalizedEmail)
        if (cancelled) return
        const nextRequiresAgreement = consentState.requiresAgreement
        setRequiresAgreement(nextRequiresAgreement)
        setAgreementChecked(!nextRequiresAgreement)
      } catch {
        if (cancelled) return
        setRequiresAgreement(true)
        setAgreementChecked(false)
        setConsentLookupError(CONSENT_LOOKUP_ERROR_MESSAGE)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [panel, email, getConsentState])

  const handleEmailSubmit = () => {
    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail) {
      setEmailError(EMAIL_REQUIRED_MESSAGE)
      return
    }
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setEmailError(EMAIL_INVALID_MESSAGE)
      return
    }

    persistLastAuthEmail(normalizedEmail)
    setEmail(normalizedEmail)
    goToPanel(EMAIL_CODE_STEP_ENABLED ? 'code' : 'password')
  }

  const handleLogin = async () => {
    if (isSubmitting) return

    setIsSubmitting(true)
    setAuthError(null)
    setAgreementError(null)
    setConsentLookupError(null)
    try {
      const normalizedEmail = email.trim().toLowerCase()
      let acceptedUserAgreementForLogin = agreementChecked
      if (EMAIL_REGEX.test(normalizedEmail)) {
        try {
          const consentState = await getConsentState(normalizedEmail)
          const nextRequiresAgreement = consentState.requiresAgreement
          setRequiresAgreement(nextRequiresAgreement)
          if (!nextRequiresAgreement) {
            setAgreementChecked(true)
            acceptedUserAgreementForLogin = true
          }

          if (nextRequiresAgreement && !agreementChecked) {
            setAgreementError(AGREEMENT_REQUIRED_MESSAGE)
            return
          }

          acceptedUserAgreementForLogin = nextRequiresAgreement ? agreementChecked : true
        } catch {
          setRequiresAgreement(true)
          setAgreementChecked(false)
          setConsentLookupError(CONSENT_LOOKUP_ERROR_MESSAGE)
          setAgreementError(AGREEMENT_REQUIRED_MESSAGE)
          return
        }
      } else {
        setRequiresAgreement(true)
        setAgreementChecked(false)
        setAgreementError(AGREEMENT_REQUIRED_MESSAGE)
        return
      }

      persistLastAuthEmail(normalizedEmail)
      setEmail(normalizedEmail)

      const response = await login({
        email: normalizedEmail,
        password,
        acceptedUserAgreement: acceptedUserAgreementForLogin,
      })

      if (isProfileStepRequired(response)) {
        unlockRegistrationProfileStep()
        setProfileStepUnlocked(true)
        setName(response.user.name ?? '')
        setTelegram(response.user.telegram ?? '')
        setCompany(response.user.company ?? '')
        setJobTitle(response.user.position ?? '')
        setRequiresAgreement(response.user.acceptedUserAgreement === false)
        setAgreementChecked(response.user.acceptedUserAgreement === true)
        setProfileError(null)
        navigate(buildRegistrationStepPath('profile', searchParams), { replace: true })
        setPanel('profile')
        return
      }

      navigate(
        resolveAuthRedirect({
          subscriptionActive: response.user.subscriptionActive,
          subscriptionStatus: response.user.subscriptionStatus,
          redirectBranch: response.redirectBranch,
          redirectPath: response.redirectPath,
        })
      )
    } catch (error) {
      const parsed = parseAuthError(error)
      const isShortPassword = password.trim().length < 8
      const isPasswordValidationResponse =
        parsed.code === 'PASSWORD_INVALID' ||
        parsed.code === 'VALIDATION_ERROR' ||
        parsed.message === 'Проверьте корректность введенных данных.' ||
        parsed.message === 'Этот пароль не действителен'

      if (isShortPassword && isPasswordValidationResponse) {
        setAuthError(
          'Проверьте корректность введенных данных. Пароль должен содержать минимум 8 символов.'
        )
        return
      }
      if (parsed.code === 'PASSWORD_NOT_SET') {
        setAuthError(MISSING_PASSWORD_MESSAGE)
        return
      }
      if (parsed.code === 'CONSENT_REQUIRED') {
        setRequiresAgreement(true)
        if (!agreementChecked) {
          setAgreementChecked(false)
        }
        setAgreementError(AGREEMENT_REQUIRED_MESSAGE)
        return
      }

      setAuthError(parsed.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleProfileSubmit = async () => {
    if (isSubmitting) return
    const normalizedProfile = {
      name: normalizeRequiredText(name),
      telegram: normalizeRequiredText(telegram),
      company: normalizeRequiredText(company),
      position: normalizeRequiredText(jobTitle),
    }
    const requiresProfileFields = userProfileIncomplete
    const requiresProfileAgreement = userRequiresAgreement
    const hasMissingField =
      !normalizedProfile.name ||
      !normalizedProfile.telegram ||
      !normalizedProfile.company ||
      !normalizedProfile.position
    if (requiresProfileFields && hasMissingField) {
      setProfileError(PROFILE_REQUIRED_MESSAGE)
      return
    }
    if (requiresProfileAgreement && !agreementChecked) {
      setAgreementError(AGREEMENT_REQUIRED_MESSAGE)
      return
    }

    setIsSubmitting(true)
    setProfileError(null)
    setAgreementError(null)
    try {
      const response = await updateProfile({
        ...(requiresProfileFields ? normalizedProfile : {}),
        ...(requiresProfileAgreement ? { acceptedUserAgreement: true } : {}),
      })
      navigate(
        resolveAuthRedirect({
          subscriptionActive: response.user.subscriptionActive,
          subscriptionStatus: response.user.subscriptionStatus,
          redirectBranch: response.redirectBranch,
          redirectPath: response.redirectPath,
        })
      )
    } catch (error) {
      const parsed = parseAuthError(error)
      if (parsed.code === 'CONSENT_REQUIRED') {
        setRequiresAgreement(true)
        setAgreementError(AGREEMENT_REQUIRED_MESSAGE)
        return
      }
      setProfileError(parsed.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const resolveNormalizedEmail = () => {
    const normalizedEmail = email.trim().toLowerCase()

    if (!normalizedEmail) {
      setPasswordPanelMessage(EMAIL_REQUIRED_MESSAGE)
      setPasswordPanelMessageTone('error')
      return null
    }

    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setPasswordPanelMessage(EMAIL_INVALID_MESSAGE)
      setPasswordPanelMessageTone('error')
      return null
    }

    persistLastAuthEmail(normalizedEmail)
    setEmail(normalizedEmail)
    return normalizedEmail
  }

  const handlePasswordResetRequest = async () => {
    if (isSubmitting) return

    const normalizedEmail = resolveNormalizedEmail()
    if (!normalizedEmail) return

    setIsSubmitting(true)
    setAuthError(null)
    setPasswordPanelMessage(null)

    try {
      await requestPasswordReset(normalizedEmail)
      setPasswordPanelMessage(PASSWORD_RESET_EMAIL_SENT_MESSAGE)
      setPasswordPanelMessageTone('success')
    } catch (error) {
      setPasswordPanelMessage(parseAuthError(error).message)
      setPasswordPanelMessageTone('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleMagicLinkRequest = async () => {
    if (isSubmitting) return

    const normalizedEmail = resolveNormalizedEmail()
    if (!normalizedEmail) return

    setIsSubmitting(true)
    setAuthError(null)
    setPasswordPanelMessage(null)

    try {
      await requestMagicLink(normalizedEmail)
      setPasswordPanelMessage(MAGIC_LINK_EMAIL_SENT_MESSAGE)
      setPasswordPanelMessageTone('success')
    } catch (error) {
      setPasswordPanelMessage(parseAuthError(error).message)
      setPasswordPanelMessageTone('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderAuthLayout = (variant: 'desktop' | 'mobile') => {
    switch (panel) {
      case 'email':
        return (
          <AuthLayout
            panel="email"
            variant={variant}
            panelProps={{
              emailValue: email,
              onEmailChange: handleEmailChange,
              emailHelperText: emailError ?? undefined,
              showAgreementCheckbox: false,
              onSubmit: handleEmailSubmit,
            }}
          />
        )
      case 'code':
        return (
          <AuthLayout
            panel="code"
            variant={variant}
            panelProps={{
              codeValue: code,
              onCodeChange: setCode,
              onSubmit: () => goToPanel('password'),
              onBack: () => goToPanel('email'),
            }}
          />
        )
      case 'password':
        return (
          <AuthLayout
            panel="password"
            variant={variant}
            panelProps={{
              emailValue: email,
              onEmailChange: handleEmailChange,
              passwordValue: password,
              onPasswordChange: handlePasswordChange,
              passwordHelperText: authError ?? consentLookupError ?? undefined,
              showAgreementCheckbox: requiresAgreement,
              agreementChecked,
              onAgreementChange: (checked) => {
                setAgreementChecked(checked)
                setAgreementError(null)
              },
              agreementErrorText: agreementError ?? undefined,
              legalLinksReturnTo,
              onSubmit: handleLogin,
              submitDisabled: isSubmitting,
              actionButtonsDisabled: isSubmitting,
              onForgotPassword: handlePasswordResetRequest,
              onMagicLink: handleMagicLinkRequest,
              footerMessageText: passwordPanelMessage ?? undefined,
              footerMessageTone: passwordPanelMessageTone,
            }}
          />
        )
      case 'profile': {
        const showProfileFields = userProfileIncomplete
        const showProfileAgreement = userRequiresAgreement || requiresAgreement
        return (
          <AuthLayout
            panel="profile"
            variant={variant}
            panelProps={{
              title: showProfileFields ? 'Создаем ваш аккаунт' : 'Завершите вход',
              nameValue: name,
              onNameChange: (value) => {
                profileDirtyRef.current = true
                setName(value)
              },
              telegramValue: telegram,
              onTelegramChange: (value) => {
                profileDirtyRef.current = true
                setTelegram(value)
              },
              jobTitleValue: jobTitle,
              onJobTitleChange: (value) => {
                profileDirtyRef.current = true
                setJobTitle(value)
              },
              companyValue: company,
              onCompanyChange: (value) => {
                profileDirtyRef.current = true
                setCompany(value)
              },
              showProfileFields,
              showAgreementCheckbox: showProfileAgreement,
              agreementChecked,
              onAgreementChange: (checked) => {
                setAgreementChecked(checked)
                setAgreementError(null)
              },
              agreementErrorText: agreementError ?? undefined,
              legalLinksReturnTo,
              submitLabel: isSubmitting ? 'Сохраняем...' : 'Продолжить',
              onSubmit: handleProfileSubmit,
            }}
          />
        )
      }
      default:
        return null
    }
  }

  return (
    <section
      className="relative w-full"
      style={{
        minHeight: '100dvh',
        width: '100%',
        backgroundImage: `url(${bgEpic})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflowX: 'hidden',
      }}
    >
      <RegistrationPageDesktop panel={panel} renderAuthLayout={renderAuthLayout} />
      <RegistrationPageMobile panel={panel} renderAuthLayout={renderAuthLayout} />
      {panel === 'profile' && profileError ? (
        <p
          className="absolute bottom-8 mx-auto text-center"
          style={{ color: '#F62226', maxWidth: 560, padding: '0 16px' }}
        >
          {profileError}
        </p>
      ) : null}
    </section>
  )
}
